import {
  BudgetRequest,
  BudgetProposal,
  BudgetRequestCreateInput,
  BudgetProposalCreateInput,
  BudgetCounterProposalCreateInput,
  GetPaginatedBudgetRequestsInput,
  PaginatedBudgetRequestsResponse,
} from '@models/budget.model';
import { graphQlClient, graphQlMultipleUpload } from '@config/graphqlClient';
import BudgetsGraphQL from '@graphql/budget.queries';

export const budgetService = {
  async getPendingBudgetRequests(): Promise<BudgetRequest[]> {
    const data: any = await graphQlClient.request(
      BudgetsGraphQL.queries.getPendingBudgetRequests,
    );

    return data.pendingBudgetRequests || [];
  },

  async getBudgetRequestDetails(
    budgetRequestId: string,
  ): Promise<BudgetRequest | null> {
    if (!budgetRequestId) throw new Error('budgetRequestId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.queries.getBudgetRequestDetails,
      { budgetRequestId },
    );

    return data.budgetRequestDetails || null;
  },

  async getBudgetProposalConversation(
    budgetRequestId: string,
  ): Promise<BudgetProposal[]> {
    if (!budgetRequestId) throw new Error('budgetRequestId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.queries.getBudgetProposalConversation,
      { budgetRequestId },
    );

    return data.budgetProposalConversation || [];
  },

  async getBudgetRequestsByTaskId(taskId: string): Promise<BudgetRequest[]> {
    if (!taskId) throw new Error('taskId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.queries.getBudgetRequestsByTaskId,
      { taskId },
    );

    return data.budgetRequestsByTaskId || [];
  },

  async createBudgetRequest(
    input: BudgetRequestCreateInput,
  ): Promise<BudgetRequest> {
    if (input.files && input.files.length > 0) {
      const { files, ...inputWithoutFiles } = input;
      const data: any = await graphQlMultipleUpload(
        files,
        'input',
        BudgetsGraphQL.mutations.createBudgetRequest,
        inputWithoutFiles,
      );

      if (!data?.createBudgetRequest)
        throw new Error('Failed to create budget request');
      return data.createBudgetRequest;
    }

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.createBudgetRequest,
      { input },
    );

    if (!data?.createBudgetRequest)
      throw new Error('Failed to create budget request');
    return data.createBudgetRequest;
  },

  async respondToBudgetRequest(
    input: BudgetProposalCreateInput,
  ): Promise<BudgetProposal> {
    if (input.files && input.files.length > 0) {
      const { files, ...inputWithoutFiles } = input;
      const data: any = await graphQlMultipleUpload(
        files,
        'input',
        BudgetsGraphQL.mutations.respondToBudgetRequest,
        inputWithoutFiles,
      );

      if (!data?.respondToBudgetRequest)
        throw new Error('Failed to respond to budget request');
      return data.respondToBudgetRequest;
    }

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.respondToBudgetRequest,
      { input },
    );

    if (!data?.respondToBudgetRequest)
      throw new Error('Failed to respond to budget request');
    return data.respondToBudgetRequest;
  },

  async createCounterProposal(
    input: BudgetCounterProposalCreateInput,
  ): Promise<BudgetProposal> {
    if (input.files && input.files.length > 0) {
      const { files, ...inputWithoutFiles } = input;
      const data: any = await graphQlMultipleUpload(
        files,
        'input',
        BudgetsGraphQL.mutations.createCounterProposal,
        inputWithoutFiles,
      );

      if (!data?.createCounterProposal)
        throw new Error('Failed to create counter proposal');
      return data.createCounterProposal;
    }

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.createCounterProposal,
      { input },
    );

    if (!data?.createCounterProposal)
      throw new Error('Failed to create counter proposal');
    return data.createCounterProposal;
  },

  async acceptBudgetProposal(proposalId: string): Promise<BudgetProposal> {
    if (!proposalId) throw new Error('proposalId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.acceptBudgetProposal,
      { proposalId },
    );

    if (!data?.acceptBudgetProposal)
      throw new Error('Failed to accept budget proposal');
    return data.acceptBudgetProposal;
  },

  async rejectBudgetProposal(proposalId: string): Promise<BudgetProposal> {
    if (!proposalId) throw new Error('proposalId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.rejectBudgetProposal,
      { proposalId },
    );

    if (!data?.rejectBudgetProposal)
      throw new Error('Failed to reject budget proposal');
    return data.rejectBudgetProposal;
  },

  async acceptBudgetRequest(budgetRequestId: string): Promise<BudgetRequest> {
    if (!budgetRequestId) throw new Error('budgetRequestId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.acceptBudgetRequest,
      { budgetRequestId },
    );

    if (!data?.acceptBudgetRequest)
      throw new Error('Failed to accept budget request');
    return data.acceptBudgetRequest;
  },

  async rejectBudgetRequest(budgetRequestId: string): Promise<BudgetRequest> {
    if (!budgetRequestId) throw new Error('budgetRequestId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.rejectBudgetRequest,
      { budgetRequestId },
    );

    if (!data?.rejectBudgetRequest)
      throw new Error('Failed to reject budget request');
    return data.rejectBudgetRequest;
  },

  async cancelBudgetRequest(budgetRequestId: string): Promise<BudgetRequest> {
    if (!budgetRequestId) throw new Error('budgetRequestId is required');

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.mutations.cancelBudgetRequest,
      { budgetRequestId },
    );

    if (!data?.cancelBudgetRequest)
      throw new Error('Failed to cancel budget request');
    return data.cancelBudgetRequest;
  },

  async getPaginatedBudgetRequestsByOrganization(
    input: GetPaginatedBudgetRequestsInput,
  ): Promise<PaginatedBudgetRequestsResponse> {
    if (!input) throw new Error('input is required');
    if (input.page < 0) throw new Error('page must be non-negative');
    if (input.limit <= 0) throw new Error('limit must be positive');

    // Drop empty filters rather than sending them: an empty `statuses` array or a
    // blank `search` would read as a filter on the wire and narrow nothing usefully.
    const search = input.search?.trim();
    const payload: GetPaginatedBudgetRequestsInput = {
      ...input,
      statuses: input.statuses?.length ? input.statuses : undefined,
      search: search ? search : undefined,
    };

    const data: any = await graphQlClient.request(
      BudgetsGraphQL.queries.paginatedBudgetRequestsByOrganization,
      { input: payload },
    );

    const result = data?.paginatedBudgetRequestsByOrganization;

    return {
      budgetRequests: result?.budgetRequests || [],
      count: result?.count || 0,
      facets: result?.facets || [],
      totalBeforeStatusFilter: result?.totalBeforeStatusFilter ?? 0,
      actionableCount: result?.actionableCount ?? 0,
    };
  },
};

export default budgetService;
